import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import DevicesIcon from "@mui/icons-material/Devices";
import { useLocation } from "react-router-dom";
import moment from "moment";
import currency from "currency.js";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import UserAvatar from "../../common/UserAvatar";
import ItemFilter from "../../planner/component/ItemFilter";
import { getStoreFilterStreets, getStoreLocationIdForFilter } from "../../common/util";

const ACCENT = "#78374a";
const MUTED = "#818181";
const ACCENT_SOFT = "#A86B7C";
const BAR_TRACK = "#E8E4E5";
const CHANNEL_COLORS = {
  online: ACCENT,
  inStore: ACCENT_SOFT,
  commercial: MUTED,
};
const TXN_COLORS = {
  card: ACCENT,
  cash: ACCENT_SOFT,
  other: MUTED,
};

/** Calendar date (YYYY-MM-DD) in America/New_York — matches server EOD bucketing. */
function easternDateKey(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function money(value) {
  return currency(value || 0).format();
}

function pct(part, whole) {
  if (!whole) return 0;
  return Math.round(((part || 0) / whole) * 100);
}

function formatUpdatedAgo(generatedAt) {
  if (!generatedAt) return "";
  const mins = Math.max(0, Math.floor((Date.now() - generatedAt) / 60000));
  if (mins < 1) return "Updated just now";
  if (mins === 1) return "Updated 1 min ago";
  return `Updated ${mins} min ago`;
}

function KpiTile({ label, value, sub }) {
  return (
    <Paper elevation={0} sx={{ p: 2, height: "100%", borderRadius: 1, bgcolor: "#FFFFFF" }}>
      <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );
}

function ShareBar({ segments, total }) {
  return (
    <Box>
      {!total ? (
        <Box sx={{ height: 8, borderRadius: 4, bgcolor: BAR_TRACK }} />
      ) : (
        <Box
          sx={{
            display: "flex",
            height: 8,
            borderRadius: 4,
            overflow: "hidden",
            bgcolor: BAR_TRACK,
          }}
        >
          {segments.map((segment) => {
            const width = pct(segment.value, total);
            if (!width) return null;
            return (
              <Box
                key={segment.key}
                title={`${segment.label}: ${money(segment.value)} (${width}%)`}
                sx={{ width: `${width}%`, bgcolor: segment.color, minWidth: 3 }}
              />
            );
          })}
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1.5 }}>
        {segments.map((segment) => (
          <Box
            key={segment.key}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
          >
            <Typography variant="body2" color="text.secondary" noWrap>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: segment.color,
                  mr: 0.75,
                }}
              />
              {segment.label}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
              {money(segment.value)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                {pct(segment.value, total)}%
              </Typography>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function MixCard({ title, segments, total }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, height: "100%", borderRadius: 1, bgcolor: "#FFFFFF" }}>
      <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <ShareBar segments={segments} total={total} />
    </Paper>
  );
}

function StatTile({ label, amount, count, detail, leading }) {
  return (
    <Paper elevation={0} sx={{ p: 2, height: "100%", borderRadius: 1, bgcolor: "#FFFFFF" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {leading ? (
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, color: MUTED }}>
            {leading}
          </Box>
        ) : null}
        <Typography variant="overline" color="text.secondary" display="block" noWrap>
          {label}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
        {money(amount)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {count || 0} {(count || 0) === 1 ? "order" : "orders"}
      </Typography>
      {detail ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
          {detail}
        </Typography>
      ) : null}
    </Paper>
  );
}

function formatPosMethods(pos) {
  const parts = [];
  if ((pos.card?.totalAmount || 0) > 0 || (pos.card?.orderCount || 0) > 0) {
    parts.push(`Card ${money(pos.card?.totalAmount)}`);
  }
  if ((pos.cash?.totalAmount || 0) > 0 || (pos.cash?.orderCount || 0) > 0) {
    parts.push(`Cash ${money(pos.cash?.totalAmount)}`);
  }
  if ((pos.other?.totalAmount || 0) > 0 || (pos.other?.orderCount || 0) > 0) {
    parts.push(`Other ${money(pos.other?.totalAmount)}`);
  }
  return parts.join(" · ");
}

function StationTile({ pos }) {
  const uid = pos.key && pos.key !== "unknown" ? pos.key : "";
  return (
    <StatTile
      label={pos.label || "POS"}
      amount={pos.totalAmount}
      count={pos.orderCount}
      detail={formatPosMethods(pos)}
      leading={
        uid ? (
          <UserAvatar uid={uid} size="sm" />
        ) : (
          <DevicesIcon sx={{ fontSize: 28, color: MUTED }} />
        )
      }
    />
  );
}

export default function OwnerDashboard() {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const todayEt = easternDateKey();
  const greetingName = (user?.firstName || user?.profile?.bizName || "").trim();

  const [report, setReport] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [storeFilter, setStoreFilter] = useState({});
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const load = (refresh = false, storeLocationId) => {
    setError("");
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const locId =
      storeLocationId !== undefined
        ? storeLocationId
        : getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    const locQ = locId ? `&storeLocationId=${encodeURIComponent(locId)}` : "";
    const refreshParam = refresh ? "&refresh=1" : "";
    Promise.all([
      bkstApi(`/my/report/end-of-day?date=${todayEt}${refreshParam}${locQ}`),
      bkstApi.get(`/my/dash`),
    ])
      .then(([eodRes, dashRes]) => {
        setReport(eodRes.data.report);
        setDash(dashRes.data);
        setLoading(false);
        setRefreshing(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load dashboard");
        setLoading(false);
        setRefreshing(false);
      });
  };

  const onStoreFilterChange = (o) => {
    const next = { ...storeFilter, ...o };
    const prevId = getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    const nextId = getStoreLocationIdForFilter(user, next?.store?.value);
    setStoreFilter(next);
    if (prevId === nextId) return;

    setError("");
    setRefreshing(true);
    const locQ = nextId ? `&storeLocationId=${encodeURIComponent(nextId)}` : "";
    bkstApi(`/my/report/end-of-day?date=${todayEt}${locQ}`)
      .then((eodRes) => {
        setReport(eodRes.data.report);
        setRefreshing(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load dashboard");
        setRefreshing(false);
      });
  };

  useEffect(() => {
    load(false);
  }, [location.key]);

  if (loading) {
    return <LinearProgress />;
  }

  const summary = report?.summary || {};
  const fulfillment = report?.fulfillment || {};
  const transactions = report?.transactions || {};
  const channel = report?.channel || {};
  const announcements = dash?.announcement || [];
  const stations = channel.inStore?.byPos || [];

  const channelSegments = [
    {
      key: "online",
      label: "Online",
      value: channel.online?.totalAmount || 0,
      color: CHANNEL_COLORS.online,
    },
    {
      key: "inStore",
      label: "In-Store",
      value: channel.inStore?.totalAmount || 0,
      color: CHANNEL_COLORS.inStore,
    },
    {
      key: "commercial",
      label: "Commercial",
      value: channel.commercial?.totalAmount || 0,
      color: CHANNEL_COLORS.commercial,
    },
  ];
  const channelTotal =
    channelSegments[0].value + channelSegments[1].value + channelSegments[2].value;

  const txnSegments = [
    { key: "card", label: "Card", value: transactions.card?.amount || 0, color: TXN_COLORS.card },
    { key: "cash", label: "Cash", value: transactions.cash?.amount || 0, color: TXN_COLORS.cash },
    {
      key: "other",
      label: "Other",
      value: transactions.other?.amount || 0,
      color: TXN_COLORS.other,
    },
  ];
  const txnTotal = transactions.total?.amount || 0;

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={3}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              {greetingName ? `Hi ${greetingName}!` : "Today"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {moment(todayEt).format("dddd, MMM D, YYYY")}
              {report?.generatedAt ? ` · ${formatUpdatedAgo(report.generatedAt)}` : ""}
            </Typography>
          </Box>
          <IconButton
            onClick={() => load(true)}
            disabled={refreshing}
            title="Refresh"
            aria-label="Refresh today's numbers"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        {refreshing ? <LinearProgress sx={{ mt: 1.5 }} /> : null}
      </Box>

      {showStoreFilter && (
        <Box my={2}>
          <ItemFilter
            config={[{ name: "store", options: storeStreets }]}
            onChange={onStoreFilterChange}
          />
        </Box>
      )}

      {announcements.map((o, idx) => (
        <Box key={idx} mb={2}>
          <Alert severity="info">{o.text}</Alert>
        </Box>
      ))}

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          mb: 2.5,
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
        }}
      >
        <KpiTile label="Total sales" value={money(summary.totalSales)} />
        <KpiTile label="Orders" value={(summary.orderCount || 0).toLocaleString()} />
        <KpiTile label="Tips" value={money(summary.tips)} />
        <KpiTile
          label="Collected"
          value={money(txnTotal)}
          sub={`${transactions.total?.count || 0} payments`}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          mb: 2.5,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        }}
      >
        <MixCard title="Channel" segments={channelSegments} total={channelTotal} />
        <MixCard title="Payments" segments={txnSegments} total={txnTotal} />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Fulfillment
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          }}
        >
          <StatTile
            label="To-Go"
            amount={fulfillment.togo?.totalAmount}
            count={fulfillment.togo?.orderCount}
            detail={fulfillment.togo?.tips ? `Tips ${money(fulfillment.togo.tips)}` : ""}
            leading={<ShoppingBagOutlinedIcon sx={{ fontSize: 28 }} />}
          />
          <StatTile
            label="Future"
            amount={fulfillment.future?.totalAmount}
            count={fulfillment.future?.orderCount}
            detail={fulfillment.future?.tips ? `Tips ${money(fulfillment.future.tips)}` : ""}
            leading={<EventOutlinedIcon sx={{ fontSize: 28 }} />}
          />
        </Box>
      </Box>

      {stations.length > 0 ? (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Stations
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            }}
          >
            {stations.map((pos) => (
              <StationTile key={pos.key || pos.label} pos={pos} />
            ))}
          </Box>
        </Box>
      ) : null}
    </Container>
  );
}
